import { GroupBase, StylesConfig } from "react-select";
import { SelectInputProps, SelectInputOption } from "./SelectInput.types";

export const getSelectInputStyling = <IsMulti extends boolean, TOptions extends SelectInputOption[]>(
  props: SelectInputProps<IsMulti, TOptions>
): StylesConfig<SelectInputOption, IsMulti, GroupBase<SelectInputOption>> => ({
  control: (base, state) => ({
    // Base
    ...base,
    cursor: "pointer",
    outline: 0,
    margin: 0,
    width: props.width ?? "100%",
    overflow: "hidden",
    //minHeight and maxHeight has to be set to control the maxHeight for the xs size only. No, I don't know why.
    //Do not remove minHeight
    minHeight: props.size === "xs" ? "36px" : props.size === "sm" ? "40px" : "44px",
    maxHeight: props.size === "xs" ? "36px" : props.size === "sm" ? "40px" : "44px",

    // Styles
    borderRadius: "8px",
    border: "1px solid var(--gray-300)",
    boxShadow: "var(--shadow-xs)",
    backgroundColor: "var(--color-base-white)",
    //icon width 20px + padding left + 8px
    padding:
      props.size === "xs"
        ? props.iconBefore
          ? "5px 11px 5px 39px"
          : "5px 11px"
        : props.size === "sm"
          ? props.iconBefore
            ? "7px 11px 7px 39px"
            : "7px 11px"
          : props.iconBefore
            ? "9px 13px 9px 41px"
            : "9px 13px",

    // States
    ...(state.isDisabled
      ? {
          borderColor: "#A19EA8",
          backgroundColor: "#F5F2F5",
        }
      : {}),
    ...(state.isFocused
      ? {
          borderColor: "var(--brand-300) !important",
          boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px rgba(158, 119, 237, 0.24)",
        }
      : {}),
  }),
  placeholder: (styles) => ({
    ...styles,
    fontSize: "var(--font-size-md)",
    lineHeight: "var(--line-height-md)",
    fontWeight: "var(--font-weight-regular)",
    color: "var(--gray-500)",
    padding: 0,
  }),
  input: (styles) => ({
    ...styles,
    fontSize: "var(--font-size-md)",
    lineHeight: "var(--line-height-md)",
    fontWeight: "var(--font-weight-regular)",
    color: "var(--gray-900)",
    padding: 0,
    margin: "0 0 0 2px", //input and text starts at the same place
  }),
  singleValue: (styles) => ({
    ...styles,
    fontSize: "var(--font-size-md)",
    lineHeight: "var(--line-height-md)",
    fontWeight: "var(--font-weight-medium)",
    color: "var(--gray-900)",
  }),

  option: (base, state) => ({
    ...base,
    fontSize: "var(--font-size-md)",
    lineHeight: "var(--line-height-md)",
    fontWeight: "var(--font-weight-medium)",
    color: "var(--gray-900)",
    cursor: "pointer",
    margin: "4px 0",
    "&:active": {
      backgroundColor: "var(--brand-50)",
    },
    display: "flex",
    alignItems: "center",
    ":before": {
      boxSizing: "border-box",
    },
    ...(state.isSelected || state.isFocused || state.isSelected
      ? {
          color: "var(--brand-700)",
          backgroundColor: "var(--brand-50)",
          borderRadius: "8px",
        }
      : {}),
  }),
  valueContainer: (base) => ({
    ...base,
    padding: 0,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 999,
    padding: "0px 6px",
    border: "1px solid #D4D4D9",
    borderRadius: "8px",
    boxShadow: "0px 10px 20px rgba(74, 61, 92, 0.15), 0px 3px 6px rgba(74, 61, 92, 0.1)",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    padding: 0,
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: 0,
  }),
  indicatorSeparator: (base) => ({
    ...base,
    display: "none", // hide the default separator
  }),
  multiValue: (base, state) => ({
    ...base,
    backgroundColor: state.data.fixed ? "var(--shadow-color)" : "white",
    border: "1px solid var(--gray-300)",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    gap: "5px",
    padding: "1px 4px",
    margin: "0px 6px 0px 0px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    fontSize: "var(--font-size-sm)",
    lineHeight: "var(--line-height-sm)",
    fontWeight: "var(--font-weight-medium)",
    color: "var(--gray-700)",
    padding: 0,
    paddingLeft: 0,
    margin: 0,
  }),
  clearIndicator: (base) => ({
    ...base,
    color: "var(--primary-text-color)",
    "&:hover": {
      color: "var(--primary-text-color)",
    },
    padding: 0,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  multiValueRemove: (base, state) => {
    if (state.data.fixed) {
      return { ...base, display: "none" };
    }
    return {
      ...base,
      padding: 0,
      color: "var(--gray-400)",
      fill: "var(--gray-400)",
      "&:hover": {
        backgroundColor: "var(--gray-100)",
        color: "var(--gray-600)",
        fill: "var(--gray-600)",
      },
    };
  },
});
